import { aiCall, isAbortError } from './aiProxy';
import { getUserProfile } from '../utils/storage';
import { CREDIT_COSTS } from '../utils/credits';

const DIY_GUIDE_SYSTEM_PROMPT = `You are Pocket Mechanic, an expert automotive technician and educator. Generate a comprehensive, personalized DIY repair/maintenance guide.

CRITICAL: Be as vehicle-specific as possible. Reference the exact year, make, and model in your steps. Include model-specific drain points, bolt locations, bolt sizes, torque specs, fluid types and capacities, OEM part numbers, and known quirks. Do NOT give generic steps that could apply to any vehicle — every step MUST reflect this specific vehicle's layout, engine, and design. If this vehicle has unusual procedures (e.g. dual drain plugs, hidden fasteners, special tools), call them out explicitly.

Adapt your language and detail level to the owner's mechanical experience:
- Beginner: Very detailed step-by-step, explain WHY each step matters, emphasize safety heavily, include photos/video search suggestions
- Intermediate: Moderate detail, acknowledge skills, focus on tips and tricks that save time
- Advanced: Technical details, torque specs, advanced techniques, skip obvious basics
- Expert: Peer-level communication, focus on edge cases, optimization, and vehicle-specific nuances

When estimating parts costs, factor in the owner's location (ZIP code) for regional pricing and parts availability.

Respond ONLY with valid JSON (no markdown, no code fences). Structure:
{
  "estimated_time": "X-Y hours",
  "savings_vs_dealer": "$X,XXX",
  "vehicle_specific_notes": [
    "Important notes, known issues, TSBs, or community wisdom about doing this specific job on this exact vehicle"
  ],
  "tools": [
    { "name": "Tool name with exact size/spec for this vehicle", "essential": true }
  ],
  "parts": [
    { "name": "Part name", "part_number": "OEM# if known", "estimated_cost": "$XX" }
  ],
  "consumables": [
    { "name": "Item name with spec (e.g. exact oil weight, fluid type)", "estimated_cost": "$XX" }
  ],
  "optional_items": [
    { "name": "Nice-to-have item" }
  ],
  "safety_warnings": [
    "Warning 1",
    "Warning 2"
  ],
  "preparation": [
    "Prep step 1",
    "Prep step 2"
  ],
  "steps": [
    {
      "number": 1,
      "title": "Step title",
      "description": "Detailed description of what to do, specific to this vehicle",
      "details": "Technical details: torque specs, bolt sizes, exact locations on this vehicle",
      "pro_tip": "Best practice, trick, or shortcut that experienced mechanics use for this specific step (empty string if none)",
      "warning": "Any step-specific warning (optional, empty string if none)"
    }
  ],
  "tips_and_tricks": [
    "Tip 1",
    "Tip 2"
  ],
  "common_mistakes": [
    "Mistake 1",
    "Mistake 2"
  ]
}`;

export async function generateDIYGuide(vehicle, maintenanceItem, signal, onStage) {
  const profile = getUserProfile();

  const mechanicalLevel = profile?.mechanicalLevel || vehicle.skillLevel || 'Intermediate';
  const zipCode = profile?.zipCode || '';

  const userPrompt = `Generate a complete DIY guide for the following maintenance service:

Vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}
Current Mileage: ${vehicle.mileage}${vehicle.vin ? `\nVIN: ${vehicle.vin}` : ''}
Service: ${maintenanceItem.service}
Difficulty: ${maintenanceItem.difficulty || 'Unknown'}
Description: ${maintenanceItem.description || ''}
Dealer Cost: ${maintenanceItem.costDealer || 'Unknown'}
Independent Shop Cost: ${maintenanceItem.costShop || 'Unknown'}
DIY Cost: ${maintenanceItem.costDIY || 'Unknown'}

Owner Mechanical Level: ${mechanicalLevel}${zipCode ? `\nOwner Location (ZIP): ${zipCode}` : ''}

Provide step-by-step instructions tailored to a ${mechanicalLevel.toLowerCase()}-level mechanic. Include specific part numbers for this vehicle when possible. Calculate savings vs dealer cost.

IMPORTANT: Include best practices, pro tips, and tricks that experienced mechanics use for this specific vehicle. Mention any vehicle-specific gotchas, unusual bolt locations, dual drain points, hidden fasteners, or things that are different from a typical job on a standard vehicle. If this is an exotic, specialty, or uncommon vehicle, go into extra detail about what makes the procedure unique.`;

  onStage?.('Analyzing service requirements...');

  try {
    onStage?.('Generating personalized guide...');

    const { result } = await aiCall({
      action: 'chat_simple',
      creditCost: CREDIT_COSTS.diyGuide,
      model: 'gpt-4o',
      messages: [
        { role: 'system', content: DIY_GUIDE_SYSTEM_PROMPT },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.4,
      max_tokens: 4000,
    }, { signal, timeout: 60000 });

    onStage?.('Finalizing your guide...');

    const cleaned = result.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
    return JSON.parse(cleaned);
  } catch (err) {
    if (isAbortError(err)) throw err;
    if (err.code === 'NO_CREDITS' || err.code === 'RATE_LIMITED') throw err;
    console.error('[DIYGuide] API call failed, using mock data:', err.message);
    return generateMockGuide(vehicle, maintenanceItem);
  }
}

/* ─── Step Challenge / Verification ─── */

const CHALLENGE_SYSTEM_PROMPT = `You are an expert automotive technician reviewing a DIY guide step that a user has questioned. The user thinks something might be wrong or unclear about this step.

Your job:
1. Carefully evaluate the step against the specific vehicle
2. If the step is incorrect, provide a corrected version
3. If the step is correct but unclear, provide a clearer version
4. If the step is perfectly fine, confirm it

Respond ONLY with valid JSON (no markdown, no code fences):
{
  "verdict": "corrected" | "clarified" | "confirmed",
  "explanation": "Brief explanation of what you found",
  "corrected_step": {
    "number": <same number>,
    "title": "Step title (updated if needed)",
    "description": "Full corrected/clarified description",
    "details": "Updated technical details",
    "warning": "Any updated warning or empty string"
  }
}

- "corrected" = the step had an actual error (wrong spec, wrong procedure, wrong tool)
- "clarified" = the step was correct but could be clearer for this vehicle
- "confirmed" = the step was accurate as-is`;

export async function challengeStep(vehicle, service, step, userConcern, signal) {
  const userPrompt = `Vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}
${vehicle.vin ? `VIN: ${vehicle.vin}` : ''}
Mileage: ${vehicle.mileage}

Service being performed: ${service}

Step ${step.number}: "${step.title}"
Current description: "${step.description}"
Current details: "${step.details || 'None'}"
Current warning: "${step.warning || 'None'}"

User's concern: "${userConcern}"

Please verify this step is correct for this specific vehicle and address the user's concern.`;

  try {
    const { result } = await aiCall({
      action: 'chat_simple',
      creditCost: CREDIT_COSTS.challengeStep,
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: CHALLENGE_SYSTEM_PROMPT },
        { role: 'user', content: userPrompt },
      ],
      temperature: 0.3,
      max_tokens: 1500,
    }, { signal, timeout: 30000 });

    const cleaned = result.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
    return JSON.parse(cleaned);
  } catch (err) {
    if (isAbortError(err)) throw err;
    if (err.code === 'NO_CREDITS' || err.code === 'RATE_LIMITED') throw err;
    console.error('[ChallengeStep] API call failed:', err.message);
    return {
      verdict: 'confirmed',
      explanation: 'Unable to verify at this time. The step appears to follow standard procedure.',
      corrected_step: { ...step },
    };
  }
}

/* ─── Mock Guide ─── */

function generateMockGuide(vehicle, item) {
  const label = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  return {
    estimated_time: '2-3 hours',
    savings_vs_dealer: '$400-600',
    tools: [
      { name: '19mm socket', essential: true },
      { name: 'Torque wrench (ft-lbs)', essential: true },
      { name: 'Jack and jack stands', essential: true },
      { name: 'C-clamp or brake piston tool', essential: true },
      { name: 'Wire brush', essential: false },
    ],
    parts: [
      { name: `Front brake pads for ${label}`, part_number: 'OEM varies', estimated_cost: '$80-150' },
    ],
    consumables: [
      { name: 'Brake cleaner (1 can)', estimated_cost: '$8' },
      { name: 'Anti-seize lubricant', estimated_cost: '$12' },
      { name: 'Brake grease', estimated_cost: '$6' },
    ],
    optional_items: [
      { name: 'Nitrile gloves' },
      { name: 'Shop rags / paper towels' },
      { name: 'Penetrating oil (PB Blaster)' },
    ],
    safety_warnings: [
      'Never work under a vehicle supported only by a jack — always use jack stands',
      'Wear safety glasses to protect from brake dust',
      'Brake dust may contain harmful particles — avoid breathing it in',
      'Allow brakes to cool before working on them if vehicle was recently driven',
    ],
    preparation: [
      'Park on a level, solid surface and engage the parking brake',
      `Loosen lug nuts on the wheel before lifting the ${label}`,
      'Locate the correct jack points for your vehicle (check owner\'s manual)',
      'Have all tools and parts organized and within reach before starting',
    ],
    steps: [
      {
        number: 1,
        title: 'Prepare the workspace',
        description: 'Park on level ground, engage parking brake. Loosen wheel lug nuts (1/4 turn only — don\'t remove yet). Place wheel chocks behind rear tires.',
        details: 'Use the breaker bar for stubborn lugs. Turn counter-clockwise.',
        warning: '',
      },
      {
        number: 2,
        title: 'Lift and secure the vehicle',
        description: 'Position the jack under the designated jack point and raise the vehicle. Place jack stand under the frame rail. Lower the vehicle onto the jack stand.',
        details: 'Refer to the owner\'s manual for exact jack point locations.',
        warning: 'Never work under a vehicle supported only by a jack.',
      },
      {
        number: 3,
        title: 'Remove the wheel',
        description: 'Remove the lug nuts completely and pull the wheel off. Set it aside.',
        details: 'Keep lug nuts together in a container so they don\'t roll away.',
        warning: '',
      },
      {
        number: 4,
        title: 'Remove the brake caliper',
        description: 'Locate the caliper slide bolts (typically 14mm or 17mm). Remove both bolts and carefully lift the caliper off the rotor.',
        details: 'The caliper will still be connected by the brake hose. Hang it with a wire or bungee cord from the suspension.',
        warning: 'NEVER let the caliper hang by the brake hose — this can damage the line.',
      },
      {
        number: 5,
        title: 'Remove old brake pads',
        description: 'Slide the old brake pads out of the caliper bracket. Note how they were installed for reference.',
        details: 'Take a photo of the pad orientation before removal for reference.',
        warning: '',
      },
      {
        number: 6,
        title: 'Compress the brake piston',
        description: 'Use a C-clamp or brake piston tool to push the caliper piston back into the housing. This creates room for the new, thicker pads.',
        details: 'Place one of the old pads against the piston face to distribute pressure evenly.',
        warning: 'Open the brake fluid reservoir cap first to prevent pressure damage.',
      },
      {
        number: 7,
        title: 'Install new brake pads',
        description: 'Apply brake grease to the pad backing plates (not the friction surface). Slide the new pads into the caliper bracket in the same orientation as the old ones.',
        details: 'Ensure the wear indicator (if present) is on the correct side (usually inboard).',
        warning: 'Never get grease or oil on the pad friction surface.',
      },
      {
        number: 8,
        title: 'Reinstall the caliper',
        description: 'Lower the caliper over the new pads and onto the bracket. Thread in the slide bolts by hand first, then torque to spec.',
        details: `Typical torque: 25-35 ft-lbs for slide bolts. Check your ${label} specific torque spec.`,
        warning: '',
      },
      {
        number: 9,
        title: 'Reinstall the wheel and lower the vehicle',
        description: 'Mount the wheel and hand-tighten the lug nuts in a star pattern. Lower the vehicle and torque the lugs to spec.',
        details: 'Typical lug nut torque: 80-100 ft-lbs. Tighten in star/cross pattern.',
        warning: '',
      },
      {
        number: 10,
        title: 'Bed in the new brake pads',
        description: 'Pump the brake pedal several times before driving to seat the pads against the rotor. Then drive slowly, making 5-6 moderate stops from 30 mph.',
        details: 'Avoid hard stops for the first 100 miles to allow proper pad transfer.',
        warning: 'The brake pedal may go to the floor on the first press — this is normal. Pump until firm.',
      },
    ],
    tips_and_tricks: [
      'Spray penetrating oil on caliper bolts 10 minutes before removal',
      'Take photos during disassembly to reference during reassembly',
      'Clean the caliper bracket slide pins and apply fresh brake grease',
      'Check rotor thickness and condition while pads are off — replace if needed',
      'Consider replacing hardware clips if they come with the new pads',
    ],
    common_mistakes: [
      'Forgetting to pump the brake pedal before driving (dangerous!)',
      'Letting the caliper hang by the brake hose',
      'Getting grease on the pad friction surface',
      'Not torquing lug nuts to spec (can cause warped rotors)',
      'Forgetting to open the brake fluid reservoir before compressing piston',
    ],
  };
}
